import { useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileUploadSection } from "@/components/onboarding/shared/FileUploadSection";
import { useGetDecizieNivelRisc } from "@/hooks/api/query/useGetDecizieNivelRisc";
import { useSalveazaDecizieNivelRisc } from "@/hooks/api/mutate/useSalveazaDecizieNivelRisc";
import { useToast } from "@/hooks/use-toast";

type DecizieNivelRiscFormProps = {
  sessionId: string | null;
};

const NIVELURI: { valoare: string; eticheta: string }[] = [
  { valoare: "de_baza", eticheta: "De bază" },
  { valoare: "important", eticheta: "Important" },
  { valoare: "esential", eticheta: "Esențial" },
];

function DecizieNivelRiscForm({ sessionId }: DecizieNivelRiscFormProps) {
  const { toast } = useToast();
  const { data, isLoading } = useGetDecizieNivelRisc(sessionId);
  const { mutate: salveaza, isPending } = useSalveazaDecizieNivelRisc();

  const [numar, setNumar] = useState<string | null>(null);
  const [dataDeciziei, setDataDeciziei] = useState<string | null>(null);
  const [nivel, setNivel] = useState<string | null>(null);
  const [fisier, setFisier] = useState<string | null>(null);

  const decizie = data?.data ?? null;
  const valoareNumar = numar ?? decizie?.decision_number ?? "";
  const valoareData = dataDeciziei ?? decizie?.decision_date ?? "";
  const valoareNivel = nivel ?? decizie?.nivel ?? "";
  const valoareFisier = fisier ?? decizie?.fisier ?? "";

  const handleUpload = async (fisiere: File[]) => {
    if (fisiere.length > 0) setFisier(fisiere[0].name);
  };

  const handleSalveaza = () => {
    if (!sessionId) return;
    if (!valoareNivel) {
      toast({
        title: "Nivelul este obligatoriu",
        description: "Alege nivelul de risc din decizia DNSC.",
        variant: "destructive",
      });
      return;
    }
    salveaza(
      {
        session_id: sessionId,
        decision_number: valoareNumar || null,
        decision_date: valoareData || null,
        nivel: valoareNivel,
        fisier: valoareFisier || null,
      },
      {
        onSuccess: () =>
          toast({ title: "Decizia de nivel de risc a fost salvată" }),
        onError: () =>
          toast({
            title: "Eroare la salvare",
            description: "Încearcă din nou.",
            variant: "destructive",
          }),
      }
    );
  };

  if (!sessionId) return null;

  return (
    <div
      data-testid="decizie-nivel-risc"
      className="space-y-4 rounded-lg border border-slate-200 p-4"
    >
      <div className="flex items-center gap-3">
        <ShieldAlert className="h-5 w-5 text-blue-600" />
        <div>
          <h2 className="font-heading text-base font-semibold text-slate-900">
            Decizie de nivel de risc
          </h2>
          <p className="text-sm text-slate-500">
            Încarcă decizia DNSC de încadrare în nivelul de risc sau completează
            manual.
          </p>
        </div>
      </div>

      <FileUploadSection
        onUpload={handleUpload}
        isExtracting={false}
        accept=".pdf"
      />

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="nivel_risc_numar">Număr decizie</Label>
          <Input
            id="nivel_risc_numar"
            value={valoareNumar}
            disabled={isLoading}
            onChange={(e) => setNumar(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="nivel_risc_data">Data deciziei</Label>
          <Input
            id="nivel_risc_data"
            type="date"
            value={valoareData}
            disabled={isLoading}
            onChange={(e) => setDataDeciziei(e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="nivel_risc_nivel">Nivel de risc</Label>
        <Select value={valoareNivel} onValueChange={setNivel}>
          <SelectTrigger id="nivel_risc_nivel">
            <SelectValue placeholder="Alege nivelul din decizie" />
          </SelectTrigger>
          <SelectContent>
            {NIVELURI.map((n) => (
              <SelectItem key={n.valoare} value={n.valoare}>
                {n.eticheta}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {valoareFisier && (
        <p className="font-mono text-xs text-slate-500">{valoareFisier}</p>
      )}

      <Button
        onClick={handleSalveaza}
        disabled={isPending || isLoading}
        data-testid="decizie-nivel-risc-salveaza"
      >
        {isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Se salvează...
          </>
        ) : (
          "Salvează nivelul de risc"
        )}
      </Button>
    </div>
  );
}

export default DecizieNivelRiscForm;
